import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, TextField, InputAdornment,
  Menu, MenuItem, CircularProgress, Alert, Select, FormControl, InputLabel,
  Drawer, Divider, Stack, Button, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon, MoreVert as MoreVertIcon, Edit as EditIcon,
  Verified as VerifiedIcon, Close as CloseIcon, Add as AddIcon,
  Delete as DeleteIcon, Person as PersonIcon,
} from '@mui/icons-material';
import iwalkcvService, { IWOperador } from '../../services/iwalkcv.service';

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  PENDING: 'warning', ACTIVE: 'success', SUSPENDED: 'error',
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendente', ACTIVE: 'Ativo', SUSPENDED: 'Suspenso',
};

const EMPTY_FORM: Partial<IWOperador> = {
  user_id: '', nome_empresa: '', descricao: '', email_contacto: '',
  telefone: '', iban: '', taxa_comissao: 15, verificado: 0, status: 'PENDING',
};

export default function OperadoresPage() {
  const [operadores, setOperadores] = useState<IWOperador[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<IWOperador | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<Partial<IWOperador>>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // User search for create
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState<{ uid: string; email: string; nome: string; telefone: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<{ uid: string; email: string; nome: string; telefone: string } | null>(null);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Edit drawer
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState<Partial<IWOperador>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete dialog
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await iwalkcvService.getOperadores({ status: filterStatus || undefined });
      setOperadores(res.data || []);
    } catch {
      setError('Erro ao carregar operadores');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const filtered = operadores.filter((o) =>
    !search || o.nome_empresa.toLowerCase().includes(search.toLowerCase())
  );

  // User search debounced
  function handleUserSearchInput(value: string) {
    setUserSearch(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 2) { setUserOptions([]); return; }
    setSearchingUsers(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const results = await iwalkcvService.searchUsers(value);
        setUserOptions(results);
      } catch (err) {
        console.error('Erro ao pesquisar utilizador Pagali:', err);
        setUserOptions([]);
      } finally {
        setSearchingUsers(false);
      }
    }, 350);
  }

  function handleUserSelect(user: { uid: string; email: string; nome: string; telefone: string } | null) {
    setSelectedUser(user);
    if (user) {
      setCreateForm((p) => ({
        ...p,
        user_id: user.uid,
        email_contacto: user.email,
        telefone: user.telefone || p.telefone,
        nome_empresa: p.nome_empresa || user.nome || '',
      }));
    } else {
      setCreateForm((p) => ({ ...p, user_id: '' }));
    }
  }

  // Create
  async function handleCreate() {
    if (!createForm.user_id || !createForm.nome_empresa) {
      setCreateError('Seleccione um utilizador e preencha o nome da empresa');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await iwalkcvService.createOperador(createForm);
      setCreateOpen(false);
      setCreateForm(EMPTY_FORM);
      setSelectedUser(null);
      setUserSearch('');
      setUserOptions([]);
      load();
    } catch (e: any) {
      setCreateError(e?.response?.data?.error || 'Erro ao criar operador');
    } finally {
      setCreating(false);
    }
  }

  // Edit
  async function openEdit(op: IWOperador) {
    setAnchorEl(null);
    const res = await iwalkcvService.getOperador(op.id);
    setEditData(res.data);
    setEditOpen(true);
    setSaveError(null);
  }

  async function handleSave() {
    if (!editData.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await iwalkcvService.updateOperador(editData.id, editData);
      setEditOpen(false);
      load();
    } catch {
      setSaveError('Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  // Delete
  async function handleDelete() {
    if (!deleteId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await iwalkcvService.deleteOperador(deleteId);
      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.response?.data?.error || 'Erro ao eliminar');
    } finally {
      setDeleting(false);
    }
  }

  // Status actions
  async function verificar(id: number) {
    await iwalkcvService.verificarOperador(id);
    load();
    setAnchorEl(null);
  }
  async function setStatus(id: number, status: string) {
    await iwalkcvService.setOperadorStatus(id, status);
    load();
    setAnchorEl(null);
  }

  const cf = (label: string, key: keyof typeof createForm, type = 'text') => (
    <TextField label={label} type={type} value={(createForm as any)[key] ?? ''} fullWidth size="small"
      onChange={(e) => setCreateForm((p) => ({ ...p, [key]: type === 'number' ? Number(e.target.value) : e.target.value }))} />
  );
  const ef = (label: string, key: keyof IWOperador, type = 'text') => (
    <TextField label={label} type={type} value={(editData as any)[key] ?? ''} fullWidth size="small"
      onChange={(e) => setEditData((p) => ({ ...p, [key]: e.target.value }))} />
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Operadores</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setCreateForm(EMPTY_FORM); setCreateError(null); setCreateOpen(true); }}
          sx={{ bgcolor: '#8FA800', '&:hover': { bgcolor: '#7a9200' } }}>
          Novo operador
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField size="small" placeholder="Pesquisar..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filterStatus} label="Estado" onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="PENDING">Pendentes</MenuItem>
            <MenuItem value="ACTIVE">Ativos</MenuItem>
            <MenuItem value="SUSPENDED">Suspensos</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {['Empresa', 'Ilha', 'Email', 'Telefone', 'Comissão', 'Exp.', 'Estado', 'Verificado', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((op) => (
                <TableRow key={op.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{op.nome_empresa}</Typography></TableCell>
                  <TableCell>{op.ilha_nome || '—'}</TableCell>
                  <TableCell>{op.email_contacto || '—'}</TableCell>
                  <TableCell>{op.telefone || '—'}</TableCell>
                  <TableCell>{op.taxa_comissao}%</TableCell>
                  <TableCell>{op.total_experiencias ?? 0}</TableCell>
                  <TableCell><Chip label={STATUS_LABEL[op.status]} color={STATUS_COLOR[op.status]} size="small" /></TableCell>
                  <TableCell>
                    {op.verificado
                      ? <Chip icon={<VerifiedIcon />} label="Verificado" color="success" size="small" variant="outlined" />
                      : <Chip label="Por verificar" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => { setSelected(op); setAnchorEl(e.currentTarget); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhum operador encontrado</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Menu de contexto */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => selected && openEdit(selected)}><EditIcon fontSize="small" sx={{ mr: 1 }} />Editar</MenuItem>
        <Divider />
        {selected && !selected.verificado && <MenuItem onClick={() => verificar(selected.id)}>✓ Verificar operador</MenuItem>}
        {selected?.status !== 'ACTIVE' && <MenuItem onClick={() => setStatus(selected!.id, 'ACTIVE')}>Ativar</MenuItem>}
        {selected?.status === 'ACTIVE' && <MenuItem onClick={() => setStatus(selected!.id, 'SUSPENDED')} sx={{ color: 'error.main' }}>Suspender</MenuItem>}
        {selected?.status === 'SUSPENDED' && <MenuItem onClick={() => setStatus(selected!.id, 'PENDING')}>Repor para Pendente</MenuItem>}
        <Divider />
        <MenuItem sx={{ color: 'error.main' }} onClick={() => { setDeleteId(selected!.id); setDeleteError(null); setAnchorEl(null); }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />Eliminar
        </MenuItem>
      </Menu>

      {/* Dialog criar operador */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Operador</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {createError && <Alert severity="error">{createError}</Alert>}

            {/* Pesquisa de utilizador Pagali */}
            <Autocomplete
              options={userOptions}
              getOptionLabel={(o) => `${o.nome || o.email} — ${o.email}`}
              isOptionEqualToValue={(a, b) => a.uid === b.uid}
              value={selectedUser}
              onChange={(_, v) => handleUserSelect(v)}
              inputValue={userSearch}
              onInputChange={(_, v) => handleUserSearchInput(v)}
              loading={searchingUsers}
              noOptionsText={userSearch.length < 2 ? 'Escreva pelo menos 2 caracteres…' : 'Nenhum utilizador encontrado'}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.uid}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{o.nome || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.email} {o.telefone ? `· ${o.telefone}` : ''}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Pesquisar utilizador Pagali *" size="small"
                  placeholder="Email, nome ou telefone…"
                  helperText={selectedUser ? `UID: ${selectedUser.uid}` : 'O utilizador deve ter conta Pagali activa e não ser operador ainda'}
                  InputProps={{ ...params.InputProps, startAdornment: <><SearchIcon sx={{ color: 'text.disabled', fontSize: 18, mr: 0.5 }} />{params.InputProps.startAdornment}</> }} />
              )}
            />

            {cf('Nome da empresa *', 'nome_empresa')}
            <TextField label="Descrição" value={createForm.descricao ?? ''} fullWidth size="small" multiline rows={2}
              onChange={(e) => setCreateForm((p) => ({ ...p, descricao: e.target.value }))} />
            <Grid container spacing={2}>
              <Grid item xs={6}>{cf('Email de contacto', 'email_contacto', 'email')}</Grid>
              <Grid item xs={6}>{cf('Telefone', 'telefone')}</Grid>
              <Grid item xs={6}>{cf('IBAN', 'iban')}</Grid>
              <Grid item xs={6}>{cf('Comissão (%)', 'taxa_comissao', 'number')}</Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado inicial</InputLabel>
                  <Select value={createForm.status || 'PENDING'} label="Estado inicial"
                    onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value as any }))}>
                    <MenuItem value="PENDING">Pendente</MenuItem>
                    <MenuItem value="ACTIVE">Ativo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Verificado</InputLabel>
                  <Select value={createForm.verificado ? '1' : '0'} label="Verificado"
                    onChange={(e) => setCreateForm((p) => ({ ...p, verificado: Number(e.target.value) }))}>
                    <MenuItem value="0">Não</MenuItem>
                    <MenuItem value="1">Sim ✓</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}
            sx={{ bgcolor: '#8FA800', '&:hover': { bgcolor: '#7a9200' } }}>
            {creating ? 'A criar...' : 'Criar operador'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog eliminar */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Eliminar operador?</DialogTitle>
        <DialogContent>
          {deleteError
            ? <Alert severity="error">{deleteError}</Alert>
            : <Typography>Só é possível eliminar operadores sem experiências. Esta acção não pode ser desfeita.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          {!deleteError && (
            <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'A eliminar...' : 'Eliminar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Drawer de edição */}
      <Drawer anchor="right" open={editOpen} onClose={() => setEditOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" px={3} py={2} borderBottom="1px solid" borderColor="divider">
          <Typography variant="h6" fontWeight={700}>Editar Operador</Typography>
          <IconButton onClick={() => setEditOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Box p={3} sx={{ overflowY: 'auto', flex: 1 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1.5}>Dados da empresa</Typography>
              <Stack spacing={2}>
                {ef('Nome da empresa *', 'nome_empresa')}
                <TextField label="Descrição" value={editData.descricao ?? ''} fullWidth size="small" multiline rows={3}
                  onChange={(e) => setEditData((p) => ({ ...p, descricao: e.target.value }))} />
              </Stack>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1.5}>Contacto & Pagamento</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>{ef('Email de contacto', 'email_contacto', 'email')}</Grid>
                <Grid item xs={6}>{ef('Telefone', 'telefone')}</Grid>
                <Grid item xs={6}>
                  <TextField label="Comissão (%)" type="number" value={editData.taxa_comissao ?? 15} fullWidth size="small"
                    onChange={(e) => setEditData((p) => ({ ...p, taxa_comissao: Number(e.target.value) }))} />
                </Grid>
                <Grid item xs={12}>{ef('IBAN', 'iban')}</Grid>
              </Grid>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1.5}>Estado</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estado</InputLabel>
                    <Select value={editData.status || 'PENDING'} label="Estado"
                      onChange={(e) => setEditData((p) => ({ ...p, status: e.target.value as any }))}>
                      <MenuItem value="PENDING">Pendente</MenuItem>
                      <MenuItem value="ACTIVE">Ativo</MenuItem>
                      <MenuItem value="SUSPENDED">Suspenso</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Verificado</InputLabel>
                    <Select value={editData.verificado ? '1' : '0'} label="Verificado"
                      onChange={(e) => setEditData((p) => ({ ...p, verificado: Number(e.target.value) }))}>
                      <MenuItem value="1">Sim ✓</MenuItem>
                      <MenuItem value="0">Não</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>
        <Box px={3} py={2} borderTop="1px solid" borderColor="divider" display="flex" gap={2} justifyContent="flex-end">
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#5B2D8E', '&:hover': { bgcolor: '#4a2370' } }}>
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
